import { useState } from 'react';
import { Box, Typography, Avatar, Button, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * ExpandableCard Component
 * Shows title and icon by default, reveals description on hover/click
 * Perfect for reducing visual clutter while keeping information accessible
 */
const ExpandableCard = ({
  title,
  description,
  icon,
  iconColor = 'primary',
  buttonText = 'Coming Soon',
  buttonColor = 'primary',
  disabled = true,
  onClick,
  onButtonClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick || handleToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <Avatar 
        sx={{ 
          bgcolor: `${iconColor}.light`, 
          mr: 2,
          flexShrink: 0,
          width: { xs: 40, sm: 48 },
          height: { xs: 40, sm: 48 },
        }}
      >
        {icon}
      </Avatar>
      
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 1 : 0 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.95rem', sm: '1rem' },
            }}
          >
            {title}
          </Typography>
          {!onClick && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              sx={{
                ml: 1,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        
        {/* Description - shows on hover or when expanded */}
        <Collapse in={expanded || hovered}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.5,
              mt: 0.5,
            }}
          >
            {description}
          </Typography>
        </Collapse>
      </Box>
      
      {onButtonClick && (
        <Button 
          variant="contained" 
          color={buttonColor}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onButtonClick();
          }}
          sx={{ 
            ml: 2,
            flexShrink: 0,
            minWidth: { xs: '100px', sm: '120px' },
          }}
        >
          {buttonText}
        </Button>
      )}
    </Box>
  );
};

export default ExpandableCard;

